import React, { useState, useEffect } from "react";
import "./ForgetVerificationCode.css";
import logo from "../assets/logo.png";
import otpImage from "../assets/otp-support.png";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

/* ================= WHATSAPP FLOAT COMPONENT ================= */
const WhatsAppFloat = ({ 
  phoneNumber = "15485825756", 
  message = "Hello! I need urgent support from safepalPay customer service for OTP verification.",
  position = "right",
  bottom = "30px",
  right = "30px",
  left = "auto",
  size = "54px",
  iconSize = "28px",
  pulseEffect = true,
  className = "",
  style = {}
}) => {
  const formattedNumber = phoneNumber.replace(/[^\d]/g, '');
  const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;
  
  const positionStyles = position === "left" 
    ? { left: left || "20px", right: "auto" }
    : { right: right || "20px", left: "auto" };

  const combinedStyles = {
    position: 'fixed',
    bottom: bottom,
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: '#25d366',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    zIndex: 10000,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    ...positionStyles,
    ...style
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={`whatsapp-float-unique ${pulseEffect ? 'pulse-unique' : ''} ${className}`}
      style={combinedStyles}
      aria-label="Chat on WhatsApp"
      title="Chat on WhatsApp for OTP assistance"
    >
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 24 24"
        fill="white"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.76.982.998-3.677-.236-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.826 9.826 0 012.9 6.994c-.004 5.45-4.438 9.88-9.888 9.88m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.333.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.333 11.893-11.893 0-3.18-1.24-6.162-3.495-8.411" />
      </svg>
    </a>
  );
};

const ForgotVerificationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [popup, setPopup] = useState({
    show: false,
    message: "",
    success: false
  });

  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // Crisp Chat Integration
  useEffect(() => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = "b5635951-f13a-4b92-95d7-c1e3666f3abf";

    window.$crisp.push(["do", "chat:show"]);

    if (!document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
    };
  }, []);

  // Pass user email to Crisp when available
  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    const userName = localStorage.getItem("userName");
    
    if (userEmail && window.$crisp) {
      window.$crisp.push(["set", "user:email", userEmail]);
      if (userName) {
        window.$crisp.push(["set", "user:nickname", userName]);
      }
    }
  }, []);

  /* ================= GET EMAIL & START TIMER ================= */
  useEffect(() => {
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem("resetEmail");

    if (stateEmail) {
      setEmail(stateEmail);
      localStorage.setItem("resetEmail", stateEmail);
    } else if (storedEmail) {
      setEmail(storedEmail);
    }

    startCountdown();
  }, [location]);

  const startCountdown = () => {
    setCountdown(30);
    setCanResend(false);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  /* ================= OTP INPUT HANDLERS ================= */
  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`fv-otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`fv-otp-${index - 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      setOtp(pasted.split(""));
    }
  };

  /* ================= VERIFY OTP ================= */
  const handleSubmit = async () => {
    const resetCode = otp.join("");

    if (resetCode.length !== 6) {
      return setPopup({
        show: true,
        message: "Please enter complete 6-digit code",
        success: false
      });
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        "https://backend-safepal.onrender.com/api/auth/verify-reset-code",
        { email, resetCode }
      );

      if (res.data.success) {
        setPopup({
          show: true,
          message: "OTP verified successfully",
          success: true
        });

        localStorage.setItem("verifiedResetCode", resetCode);

        setTimeout(() => {
          navigate("/newpassword", { state: { email, resetCode } });
        }, 1500);
      }
    } catch (err) {
      setPopup({
        show: true,
        message: err.response?.data?.error || err.response?.data?.message || "Invalid or expired OTP",
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= RESEND OTP ================= */
  const handleResendCode = async () => {
    if (!canResend || !email) return;

    setIsLoading(true);

    try {
      const res = await axios.post(
        "https://backend-safepal.onrender.com/api/auth/resend-forgot-password-otp",
        { email }
      );

      if (res.data.success) {
        setPopup({
          show: true,
          message: "New reset code sent to your email",
          success: true
        });

        setOtp(["", "", "", "", "", ""]);
        startCountdown();
      }
    } catch (err) {
      setPopup({
        show: true,
        message: err.response?.data?.error || err.response?.data?.message || "Failed to resend code",
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fv-forgot-verification-container-unique">
        <div className="fv-forgot-verification-card-unique">
          <span className="fv-getstarted-back-unique" onClick={() => navigate(-1)}>
            ←
          </span>

          <div className="fv-forgot-verification-logo-unique">
            <img src={logo} alt="SafepalPay" />
          </div>

          {/* OTP Illustration */}
          <div className="fv-forgot-verification-illustration-unique">
            <img src={otpImage} alt="OTP Verification" />
          </div>

          <h2 className="fv-forgot-verification-title-unique">Verification Code</h2>

          <p className="fv-forgot-verification-text-unique">
            We have sent a verification code to your email. Please check your
            Spam or Junk folder if it doesn't appear shortly.
          </p>

          {email && (
            <p className="fv-forgot-verification-email-unique">
              <strong>{email}</strong>
            </p>
          )}

          <div
            className="fv-forgot-verification-otp-boxes-unique"
            onPaste={handlePaste}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`fv-otp-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                disabled={isLoading}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            className="fv-forgot-verification-submit-btn-unique"
            onClick={handleSubmit}
            disabled={otp.join("").length !== 6 || isLoading}
          >
            {isLoading ? "Verifying..." : "Submit"}
          </button>

          <p className="fv-forgot-verification-resend-text-unique">
            Didn't receive code?{" "}
            {canResend ? (
              <button
                className="fv-forgot-verification-resend-btn-unique"
                onClick={handleResendCode}
                disabled={isLoading}
              >
                Resend Code
              </button>
            ) : (
              <span className="fv-countdown-text-unique">
                Resend in {countdown}s
              </span>
            )}
          </p>
        </div>

        {/* ANIMATED POPUP */}
        {popup.show && (
          <div className="fv-forgot-verification-popup-overlay-unique">
            <div className="fv-forgot-verification-popup-card-unique">
              <div className={`fv-forgot-verification-icon-box-unique ${popup.success ? "success-unique" : "error-unique"}`}>
                <svg viewBox="0 0 100 100" className="fv-forgot-verification-icon-unique">
                  <circle cx="50" cy="50" r="45" className="fv-forgot-verification-circle-unique" />
                  <path
                    className="fv-forgot-verification-path-unique"
                    d={
                      popup.success
                        ? "M30 52 L45 65 L70 38"
                        : "M35 35 L65 65 M65 35 L35 65"
                    }
                  />
                </svg>
              </div>
              <p className="fv-forgot-verification-popup-text-unique">{popup.message}</p>
              <button
                className="fv-forgot-verification-ok-btn-unique"
                onClick={() => setPopup({ ...popup, show: false })}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Float Button - For OTP Assistance */}
      <WhatsAppFloat 
        phoneNumber="15485825756"
        message="Hello! I need urgent support from safepalPay customer service for OTP verification."
        position="right"
        bottom="30px"
        right="30px"
        pulseEffect={true}
        size="54px"
        iconSize="28px"
      />
    </>
  );
};

export default ForgotVerificationCode;